import { auth, functions, db } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { doc, updateDoc, serverTimestamp, deleteField } from 'firebase/firestore';
import { appendReportAuditEntry } from './reportAuditHelpers';

/**
 * Regenerate ALL AI outputs for the Clinical Interview stage.
 *
 * - Clears all existing paragraph approvals so every paragraph returns to
 *   "needs review" (purple) state — the review page (RD_CI) determines review
 *   status by checking which paragraph codes exist in `approvedParagraphs`;
 *   without clearing this map, paragraphs whose AI output didn't change would
 *   incorrectly remain approved.
 * - Sets processing status to 'processing' before starting
 * - Calls GenerateDraftWithLLM for each provided CI section title with force flag
 * - Waits for all generations to complete (no fire-and-forget)
 * - Sets processing status to 'complete' on success or 'error' on failure
 *
 * Note: We pass force_regenerate_keys: ['__ALL__'] so the backend regenerates all
 * paragraphs for each section regardless of previously stored inputs/edits.
 */
export const TriggerRegenerateCIStage = async ({ patientId, clinicianId, sectionTitles }) => {
  const user = auth.currentUser;
  if (!user || !user.uid) return { success: false, error: 'User not authenticated' };

  if (!patientId || !clinicianId || !Array.isArray(sectionTitles) || sectionTitles.length === 0) {
    return { success: false, error: 'Missing or invalid parameters' };
  }

  const patientDocRef = doc(db, 'patients', patientId);
  const GenerateDraftWithLLM = httpsCallable(functions, 'GenerateDraftWithLLM');

  // Helper: per-call retry with simple backoff
  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
  const callWithRetry = async (params, maxRetries = 2) => {
    let attempt = 0;
    // small stagger to reduce concurrency spikes server-side
    await delay(400);
    while (attempt <= maxRetries) {
      try {
        const res = await GenerateDraftWithLLM(params);
        return res;
      } catch (err) {
        // If deadline-exceeded, surface as error (we're awaiting completion)
        if (err?.code === 'deadline-exceeded') {
          throw err;
        }
        if (attempt === maxRetries) throw err;
        await delay(1000 * (attempt + 1));
        attempt += 1;
      }
    }
  };

  try {
    // 1) Flag as processing and wipe all prior approvals so every paragraph
    //    returns to "needs review" once the clinician opens the review page.
    //    CI uses MODULE_NUMBER = 1 in the review document components.
    await updateDoc(patientDocRef, {
      'dataModel.6_manageReports.ciReportProcessingStatus': 'processing',
      'dataModel.6_manageReports.ciReportProcessingStartedAt': serverTimestamp(),
      'dataModel.6_manageReports.sections.1.approvedParagraphs': {},
      'dataModel.6_manageReports.ciReportApproved': false,
      'dataModel.6_manageReports.ciReportApprovedBy': null,
      'dataModel.6_manageReports.ciReportApprovedByName': null,
      'dataModel.6_manageReports.ciReportApprovedAt': null,
      // The Semble MDT Report's clinical-history paragraph is a
      // summary-of-summaries of the CI; regenerating the CI makes it stale, so
      // clear its paragraph approval and reset the document-level flags to
      // force a regenerate + re-review (WLC only; inert for other orgs).
      'dataModel.6_manageReports.sections.10.approvedParagraphs.semble_clinical_history': deleteField(),
      'dataModel.6_manageReports.sembleMDTReportGenerated': false,
      'dataModel.6_manageReports.sembleMDTReportApproved': false,
      'dataModel.6_manageReports.sembleMDTReportApprovedBy': null,
      'dataModel.6_manageReports.sembleMDTReportApprovedByName': null,
      'dataModel.6_manageReports.sembleMDTReportApprovedAt': null,
    });

    appendReportAuditEntry(patientId, {
      module: 'ci',
      eventType: 'approvalReset',
      isFirstOccurrence: false,
    });

    // 2) Sequentially regenerate each visible CI section with force flag
    for (const sectionTitle of sectionTitles) {
      await callWithRetry({
        clinicianId,
        patientId,
        reportStage: 'Clinical Interview',
        reportSection: sectionTitle,
        isTest: false,
        // sentinel instructs backend to force regenerate all paragraphs
        force_regenerate_keys: ['__ALL__']
      });
    }

    // 3) Mark processing complete so dashboard moves from orange to purple
    await updateDoc(patientDocRef, {
      'dataModel.6_manageReports.ciReportProcessingStatus': 'complete',
      // Ensure generated flag is set (in case it somehow wasn't before)
      'dataModel.6_manageReports.ciReportGenerated': true
    });

    return { success: true };
  } catch (error) {
    // On any error, mark error state for UI retry affordance
    try {
      await updateDoc(patientDocRef, {
        'dataModel.6_manageReports.ciReportProcessingStatus': 'error'
      });
    } catch (_) {}
    return { success: false, error };
  }
};


